import {
  getEvaluation,
  getEvaluationRun,
  listEvaluations,
  listEvaluationSuites,
} from "@/gen_src/proto/chalk/evaluation/v1/service-EvaluationService_connectquery";
import { QueryParam } from "@/utils/hooks/url/use-query-state";
import { useQuery } from "@connectrpc/connect-query";
import { useRouter } from "next/router";
import { useMemo } from "react";

export interface EvaluationBreadcrumbSuite {
  id: string;
  name: string;
}

export interface EvaluationBreadcrumbEvaluation {
  id: string;
  name: string;
}

export interface EvaluationBreadcrumbData {
  evaluationId?: string;
  evaluationName?: string;
  evaluations: readonly EvaluationBreadcrumbEvaluation[];
  runId?: string;
  suiteId?: string;
  suiteName?: string;
  suites: readonly EvaluationBreadcrumbSuite[];
  view: "detail" | "overview" | "run-results" | "suites";
}

const getStringQueryParam = (value: string | string[] | undefined): string | undefined =>
  Array.isArray(value) ? value[0] : value;

/** Resolves the suite, evaluation, and run labels used by the evaluations top-rail breadcrumb. */
export const useEvaluationBreadcrumbData = (): EvaluationBreadcrumbData | null => {
  const router = useRouter();
  const onOverviewPage = router.pathname.endsWith("/evaluations");
  const onSuitePage = router.pathname.endsWith("/evaluations/suite/[evaluationSuiteId]");
  const onDetailPage = router.pathname.endsWith("/evaluations/[evaluationId]");
  const onEvaluationPage = onOverviewPage || onSuitePage || onDetailPage;
  const routeEvaluationId = onDetailPage
    ? getStringQueryParam(router.query.evaluationId)
    : undefined;
  const runId = onEvaluationPage
    ? getStringQueryParam(router.query[QueryParam.EVALUATION_RUN_ID])
    : undefined;
  const requestedSuiteId = onSuitePage
    ? getStringQueryParam(router.query.evaluationSuiteId)
    : getStringQueryParam(router.query[QueryParam.EVALUATION_SUITE_ID]);
  const suitesQuery = useQuery(listEvaluationSuites, {}, { enabled: onEvaluationPage });
  const runQuery = useQuery(
    getEvaluationRun,
    { evaluationRunId: runId ?? "" },
    { enabled: onOverviewPage && runId != null },
  );
  const evaluationId = routeEvaluationId ?? runQuery.data?.evaluationRun?.evaluationId;
  const evaluationQuery = useQuery(
    getEvaluation,
    { evaluationId: evaluationId ?? "" },
    { enabled: onEvaluationPage && evaluationId != null },
  );
  const evaluation = evaluationQuery.data?.evaluation;
  const responseSuites = suitesQuery.data?.suites;
  const suites = useMemo(
    () => (responseSuites ?? []).map((suite) => ({ id: suite.id, name: suite.name })),
    [responseSuites],
  );
  const suiteId = evaluation?.suiteId || suites.find((suite) => suite.id === requestedSuiteId)?.id;
  const evaluationsQuery = useQuery(
    listEvaluations,
    { limit: 100, suiteId },
    { enabled: onEvaluationPage && evaluationId != null && suiteId != null },
  );
  const responseEvaluations = evaluationsQuery.data?.evaluations;

  return useMemo(() => {
    if (!onEvaluationPage) return null;

    const suiteName = suites.find((suite) => suite.id === suiteId)?.name;
    const evaluations = (responseEvaluations ?? []).map((candidate) => ({
      id: candidate.id,
      name: candidate.name,
    }));
    if (evaluation != null && !evaluations.some((candidate) => candidate.id === evaluation.id)) {
      evaluations.unshift({ id: evaluation.id, name: evaluation.name });
    }

    return {
      evaluationId,
      evaluationName: evaluation?.name,
      evaluations,
      runId,
      suiteId,
      suiteName,
      suites,
      view:
        runId != null
          ? "run-results"
          : onDetailPage
            ? "detail"
            : requestedSuiteId == null
              ? "suites"
              : "overview",
    };
  }, [
    evaluation,
    evaluationId,
    onDetailPage,
    onEvaluationPage,
    requestedSuiteId,
    responseEvaluations,
    runId,
    suiteId,
    suites,
  ]);
};
